'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { FaCheck, FaCertificate, FaBook, FaShip, FaAnchor, FaLifeRing, FaFire, FaMedkit, FaUserShield, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { CACHE_CONTROL_HEADERS, DEFAULT_CONTACT_INFO } from '@/lib/constants';
import { getApiUrl, getCacheHeaders } from '@/lib/site-config';

interface ContactInfo {
  company_name: string;
  address_line1: string;
  address_line2: string;
  phone1: string;
  phone2: string;
  email: string;
  opening_hours: string;
  bank_info: string;
}

interface AboutSection {
  id: number;
  title: string;
  content: string;
  section_name: string;
  section_order: number;
}

export default function AboutPage() {
  const [aboutSections, setAboutSections] = useState<AboutSection[]>([]);
  const [contactInfo, setContactInfo] = useState<ContactInfo>(DEFAULT_CONTACT_INFO);
  const [dataSource, setDataSource] = useState<string>('');
  const [lastRefresh, setLastRefresh] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refreshCount, setRefreshCount] = useState<number>(0);

  // Funkcja do pobierania danych z API
  const fetchData = useCallback(async (forceRefresh = false) => {
    setIsLoading(true);
    
    // Generuj znacznik czasu, aby zapobiec buforowaniu
    const timestamp = Date.now();
    
    try {
      // Pobierz dane kontaktowe z API
      const contactBaseUrl = getApiUrl('contactInfo');
      const contactSeparator = contactBaseUrl.includes('?') ? '&' : '?';
      const contactResponse = await fetch(`${contactBaseUrl}${contactSeparator}t=${timestamp}&force=${forceRefresh}`, {
        method: 'GET',
        headers: {
          ...getCacheHeaders()
        }
      });
      
      if (contactResponse.ok) {
        const contactData = await contactResponse.json();
        if (contactData && contactData.success && contactData.contactInfo) {
          setContactInfo(contactData.contactInfo);
        }
      }
      
      // Pobierz sekcje "O nas" z API
      const aboutBaseUrl = getApiUrl('aboutUs');
      const aboutSeparator = aboutBaseUrl.includes('?') ? '&' : '?';
      const aboutResponse = await fetch(`${aboutBaseUrl}${aboutSeparator}t=${timestamp}&force=${forceRefresh}`, {
        method: 'GET',
        headers: {
          ...getCacheHeaders()
        }
      });
      
      if (aboutResponse.ok) {
        const aboutData = await aboutResponse.json();
        if (aboutData && aboutData.success && aboutData.sections) {
          setAboutSections(aboutData.sections);
          setDataSource(aboutData.source || 'nieznane');
          setLastRefresh(new Date().toLocaleString('pl'));
          setRefreshCount(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Błąd podczas pobierania danych:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Efekt przy pierwszym renderowaniu - pobierz dane
  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // Auto-refresh removed - static content doesn't need frequent updates
  // Data is fetched once on page load

  // Helper function to find section by name
  const getSectionByName = (name: string): AboutSection | undefined => {
    return aboutSections.find(section => section.section_name === name);
  };

  // Helper function to render section content
  const renderSectionContent = (name: string): React.ReactNode => {
    const section = getSectionByName(name);
    if (!section) return null;
    
    return (
      <>
        <h2 className="text-3xl font-bold text-[rgb(6,40,95)] mb-6">{section.title}</h2>
        <div className="prose max-w-none text-gray-700">
          {section.content.split('\n').map((paragraph, index) => (
            <p key={index} className="mb-4 text-lg">
              {paragraph}
            </p>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 bg-[rgb(6,40,95)]">
        <div className="absolute inset-0 z-0 opacity-20">
          <Image
            src="/images/front01.webp"
            alt="Tło szkolenia morskiego"
            fill
            className="object-cover"
            priority
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Ośrodek Szkolenia Morskiego LIBRA
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-100 max-w-3xl mx-auto"
          >
            {getSectionByName('hero_title')?.content || 'Szkolenia i certyfikacje dla pracowników gospodarki morskiej'}
          </motion.p>
        </div>
      </section>

      {/* Główna treść */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-md overflow-hidden p-8 mb-12">
            {renderSectionContent('main')}
          </div>

          {/* Logo Ośrodka */}
          <div className="flex justify-center mb-12">
            <div className="bg-white p-8 rounded-xl shadow-md flex flex-col items-center">
              <div className="relative w-48 h-48 mb-6">
                <Image
                  src="/images/logoczerwone.webp"
                  alt="Ośrodek Szkolenia Morskiego Logo"
                  fill
                  className="object-contain"
                  priority
                />
              </div>
              <h3 className="text-2xl font-bold text-[rgb(6,40,95)] text-center">
                {contactInfo.company_name}
              </h3>
              <p className="text-gray-600 mt-3 text-center max-w-lg">
                {getSectionByName('about_company')?.content || 'Od wielu lat zapewniamy najwyższej jakości szkolenia morskie, posiadając niezbędne certyfikaty i uznanie instytucji krajowych oraz międzynarodowych.'}
              </p>
              
              {/* Przyciski social media */}
              <div className="flex gap-4 mt-6">
                <a 
                  href="https://www.facebook.com/szkoleniamorskie"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-[#1877F2] hover:bg-[#1877F2]/90 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </a>
                <a 
                  href="https://www.instagram.com/szkoleniamorskie/#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                  Instagram
                </a>
              </div>
            </div>
          </div>

          {/* Karty certyfikatów */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-xl shadow-md"
            >
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-blue-100 text-[rgb(6,40,95)]">
                  <FaCertificate className="w-6 h-6" />
                </div>
                <h3 className="ml-4 text-xl font-semibold text-gray-800">Certyfikaty i uznania</h3>
              </div>
              <ul className="space-y-2">
                {getSectionByName('certificates_recognition')?.content.split('\n').map((item, index) => (
                  <li key={index} className="flex items-start">
                    <FaCheck className="w-5 h-5 text-green-500 mt-0.5 mr-2" />
                    <span>{item}</span>
                  </li>
                )) || (
                  <>
                    <li className="flex items-start">
                      <FaCheck className="w-5 h-5 text-green-500 mt-0.5 mr-2" />
                      <span>Uznanie Ministerstwa Infrastruktury</span>
                    </li>
                    <li className="flex items-start">
                      <FaCheck className="w-5 h-5 text-green-500 mt-0.5 mr-2" />
                      <span>Certyfikat ISO 9001 zatwierdzony przez Biuro Certyfikacji Polskiego Rejestru Statków S.A.</span>
                    </li>
                    <li className="flex items-start">
                      <FaCheck className="w-5 h-5 text-green-500 mt-0.5 mr-2" />
                      <span>Zgodność z wymogami konwencji STCW-78/95</span>
                    </li>
                  </>
                )}
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-xl shadow-md"
            >
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-blue-100 text-[rgb(6,40,95)]">
                  <FaBook className="w-6 h-6" />
                </div>
                <h3 className="ml-4 text-xl font-semibold text-gray-800">Dokumenty i uprawnienia</h3>
              </div>
              <p className="mb-4">
                {getSectionByName('documents_permissions')?.content.split('\n')[0] || 
                  'Na podstawie odbytych w Ośrodku przeszkoleń, Polska Administracja Morska wystawia międzynarodowe dokumenty uprawniające do pracy na statkach morskich w kraju i na całym świecie.'}
              </p>
              <div className="flex items-center text-[rgb(6,40,95)]">
                <FaShip className="w-5 h-5 mr-2" />
                <span className="font-medium">
                  {getSectionByName('documents_permissions')?.content.split('\n')[1] || 'Międzynarodowe uznanie kwalifikacji'}
                </span>
              </div>
            </motion.div>
          </div>

          {/* Sekcja z certyfikatami */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden p-8 mb-12">
            {renderSectionContent('certificates')}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white p-4 rounded-xl shadow-sm flex flex-col items-center"
              >
                <div className="relative aspect-[3/4] w-full max-w-[250px] mb-4">
                  <Image
                    src="/images/certyfikat1.webp"
                    alt="Certyfikat ISO 9001"
                    fill
                    className="object-contain"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 text-center">Certyfikat ISO 9001</h3>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white p-4 rounded-xl shadow-sm flex flex-col items-center"
              >
                <div className="relative aspect-[3/4] w-full max-w-[250px] mb-4">
                  <Image
                    src="/images/certyfikat2.webp"
                    alt="Uznanie Ministerstwa Infrastruktury"
                    fill
                    className="object-contain"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 text-center">Uznanie Ministerstwa Infrastruktury</h3>
              </motion.div>
            </div>
          </div>

          {/* Sekcja z ofertą szkoleń */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden p-8 mb-12">
            {renderSectionContent('trainings')}
            
            <div className="mb-8 mt-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <FaAnchor className="mr-2 text-[rgb(6,40,95)]" />
                Podstawowe przeszkolenia z zakresu bezpieczeństwa i ochrony
              </h3>
              <ul className="space-y-2 pl-10 list-disc text-gray-700">
                {getSectionByName('basic_safety_training')?.content.split('\n').map((item, index) => (
                  <li key={index}>{item}</li>
                )) || (
                  <>
                    <li>Przeszkolenie w zakresie indywidualnych technik ratunkowych</li>
                    <li>Przeszkolenie w zakresie ochrony przeciwpożarowej – stopień podstawowy</li>
                    <li>Przeszkolenie w zakresie elementarnych zasad udzielania pierwszej pomocy medycznej</li>
                    <li>Przeszkolenie w zakresie bezpieczeństwa własnego i odpowiedzialności wspólnej</li>
                    <li>Przeszkolenie w zakresie problematyki ochrony na statku</li>
                  </>
                )}
              </ul>
            </div>

            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <FaLifeRing className="mr-2 text-[rgb(6,40,95)]" />
                Przeszkolenia z zakresu bezpieczeństwa i ochrony
              </h3>
              <ul className="space-y-2 pl-10 list-disc text-gray-700">
                {getSectionByName('safety_training')?.content.split('\n').map((item, index) => (
                  <li key={index}>{item}</li>
                )) || (
                  <>
                    <li>Przeszkolenie w zakresie udzielania pierwszej pomocy medycznej - stopień wyższy</li>
                    <li>Przeszkolenie w zakresie sprawowania opieki medycznej nad chorym - Medical Care</li>
                    <li>Przeszkolenie w zakresie ochrony przeciwpożarowej - stopnień wyższy</li>
                    <li>Przeszkolenie w celu uzyskania świadectwa ratownika</li>
                    <li>Przeszkolenie w celu uzyskania świadectwa starszego ratownika</li>
                    <li>Przeszkolenie w zakresie bezpieczeństwa dla członków załóg łodzi rybackich w żegludze krajowej</li>
                    <li>Przeszkolenie dla członków załóg z przydzielonymi obowiązkami w zakresie ochrony</li>
                    <li>Przeszkolenie oficera ochrony statku</li>
                    <li>Przeszkolenia uaktualniające w różnych zakresach</li>
                  </>
                )}
              </ul>
            </div>

            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <FaUserShield className="mr-2 text-[rgb(6,40,95)]" />
                Dodatkowe przeszkolenia specjalistyczne
              </h3>
              <ul className="space-y-2 pl-10 list-disc text-gray-700">
                {getSectionByName('specialized_training')?.content.split('\n').map((item, index) => (
                  <li key={index}>{item}</li>
                )) || (
                  <>
                    <li>Przeszkolenie w zakresie kierowania tłumem</li>
                    <li>Przeszkolenie z zakresu bezpieczeństwa dla członków załóg bezpośrednio obsługujących pasażerów w pomieszczeniach pasażerskich</li>
                    <li>Przeszkolenie w zakresie zarządzania kryzysowego i zachowań ludzkich</li>
                    <li>Przeszkolenie w zakresie bezpieczeństwa pasażerów i ładunku oraz szczelności kadłuba na statku pasażerskim typu ro-ro</li>
                    <li>W zakresie przewozu ładunków niebezpiecznych</li>
                    <li>W zakresie znajomości Międzynarodowego kodeksu zarządzania bezpieczną eksploatacją statków i zapobieganiem zanieczyszczeniu</li>
                    <li>Oficer ochrony obiektów portowych /PFSO/</li>
                    <li>Oficer ochrony armatora /CSO/</li>
                  </>
                )}
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Dodatkowe szkolenia</h3>
              <ul className="space-y-2 pl-10 list-disc text-gray-700">
                {getSectionByName('additional_training')?.content.split('\n').map((item, index) => (
                  <li key={index}>{item}</li>
                )) || (
                  <>
                    <li>Przygotowujące do egzaminu przed Komisją Urzędu Komunikacji Elektronicznej na "Świadectwo operatora łączności bliskiego zasięgu – SRC" oraz "Operatora radiotelefonisty VHF"</li>
                    <li>Teoretyczne i praktyczne przygotowujące do egzaminów przed Komisją Polskiego Związku Motorowodnego i Narciarstwa Wodnego na patenty i licencje motorowodne</li>
                  </>
                )}
              </ul>
            </div>
          </div>

          {/* Informacja o zniżkach */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-[rgb(6,40,95)]/10 p-6 rounded-xl shadow-md mb-12"
          >
            <h3 className="text-xl font-bold text-[rgb(6,40,95)] mb-4">Promocje i zniżki</h3>
            <p className="text-gray-700">
              {getSectionByName('promotions')?.content || 
                'Dla grup zorganizowanych powyżej 10–ciu osób istnieje możliwość negocjacji cen kursu oraz zmiany terminów szkoleń i dodatkowych kursów.'}
            </p>
          </motion.div>

          {/* Sekcja o współpracy z OMK */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white p-6 rounded-xl shadow-md mb-12"
          >
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <div className="w-full md:w-1/4 flex justify-center">
                <div className="relative w-48 h-48">
                  <Image
                    src="/images/omk-logo.png"
                    alt="Logo Organizacji Marynarzy Kontraktowych NSZZ Solidarność"
                    fill
                    className="object-contain"
                  />
                </div>
              </div>
              <div className="w-full md:w-3/4">
                <h3 className="text-xl font-bold text-[rgb(6,40,95)] mb-4">
                  {getSectionByName('omk_cooperation')?.title || 'Organizacja Marynarzy Kontraktowych NSZZ "Solidarność"'}
                </h3>
                <div className="text-gray-700">
                  {getSectionByName('omk_cooperation')?.content.split('\n').map((paragraph, index) => (
                    <p key={index} className="mb-4">
                      {paragraph.includes('www.omk.org.pl') ? (
                        <>
                          Więcej informacji można uzyskać pod adresem{' '}
                          <a href="https://www.omk.org.pl" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            www.omk.org.pl
                          </a>
                        </>
                      ) : paragraph}
                    </p>
                  )) || (
                    <>
                      <p className="mb-4">
                        Współpracujemy z Organizacją Marynarzy Kontraktowych NSZZ "Solidarność".
                      </p>
                      <p className="mb-4">
                        Więcej informacji można uzyskać pod adresem{' '}
                        <a href="https://www.omk.org.pl" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          www.omk.org.pl
                        </a>
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Kontakt */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden p-8">
            <h2 className="text-3xl font-bold text-[rgb(6,40,95)] mb-6">Kontakt i zapisy</h2>
            <p className="text-gray-700 mb-4">
              Informacje i zapisy w biurze Ośrodka od poniedziałku do piątku w godzinach 9:00 - 15:00
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Dane kontaktowe</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>{contactInfo.company_name}</li>
                  <li>{contactInfo.address_line1}</li>
                  <li>{contactInfo.address_line2}</li>
                  <li>{contactInfo.phone1}</li>
                  <li>{contactInfo.phone2}</li>
                  <li className="flex items-center">
                    <span className="inline-block">Email:&nbsp;</span>
                    <a href={`mailto:${contactInfo.email}`} className="text-blue-600 hover:underline whitespace-nowrap overflow-hidden text-overflow-ellipsis inline-block max-w-full">{contactInfo.email}</a>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Godziny otwarcia</h3>
                <ul className="space-y-2 text-gray-700">
                  {contactInfo.opening_hours.split('\n').map((line: string, index: number) => {
                    const parts = line.split(':');
                    if (parts.length > 1) {
                      return (
                        <li key={index}>
                          <span className="font-medium">{parts[0]}:</span>{parts.slice(1).join(':')}
                        </li>
                      );
                    }
                    return <li key={index}>{line}</li>;
                  })}
                </ul>
                <p className="mt-4 text-gray-700">
                  Szkolenia odbywają się zgodnie z harmonogramem. Zapraszamy do sprawdzenia aktualnych terminów w zakładce Harmonogram.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg md:col-span-2">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Dane bankowe</h3>
                <ul className="space-y-2 text-gray-700">
                  {contactInfo.bank_info.split('\n').map((line: string, index: number) => (
                    <li key={index}>{line}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informacja o źródle danych - widoczna tylko w trybie deweloperskim */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 bg-white p-3 rounded-lg shadow-md text-sm text-gray-600 z-50">
          <p>Źródło danych: <strong>{dataSource}</strong></p>
          <p>Ostatnie odświeżenie: <strong>{lastRefresh}</strong></p>
          <p>Liczba odświeżeń: <strong>{refreshCount}</strong></p>
          {isLoading && <p className="text-blue-500">Trwa odświeżanie...</p>}
        </div>
      )}
    </div>
  );
} 